import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, MessageCircle, PawPrint, Search, ChevronRight, Shield, Plus, X, Pencil, Check, CheckCircle, Inbox, Send, Dog, UserCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { API_BASE } from '@/lib/api';
import CareRequestsClient from '@/components/CareRequestsClient';
import SubscriptionCard from '@/components/SubscriptionCard';
import { PetForm } from './PetSetup';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const firstNameOnly = (value) => (value || '').trim().split(/\s+/)[0] || 'Cuidador';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PET_SIZE_LABEL = { pequeno: 'Pequeño', mediano: 'Mediano', grande: 'Grande' };

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [pets, setPets] = useState([]);
  const [clientReviews, setClientReviews] = useState([]);
  const [contactRequestsSent, setContactRequestsSent] = useState([]);
  const [stats, setStats] = useState({
    activeRequests: 0,
    pendingProposals: 0,
    unreadMessages: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'requests');
  const [showOnboardingRequest, setShowOnboardingRequest] = useState(false);
  const [checkedOnboarding, setCheckedOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState('request');
  const [onboardingPetId, setOnboardingPetId] = useState('');
  
  // Pet management states
  const [showPetForm, setShowPetForm] = useState(false);
  const [newPet, setNewPet] = useState({ name: '', type: '', breed: '', size: '', age: '', notes: '' });
  const [savingPet, setSavingPet] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [editPetData, setEditPetData] = useState({});

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);

    if (searchParams.get('newClientPet') === '1') {
      setActiveTab('pets');
      setShowPetForm(true);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [userRes, subRes, favRes, petRes, reviewRes, careReqRes, propRes, convRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/subscription/my').catch(() => ({ data: { has_subscription: false } })),
        api.get('/favorites').catch(() => ({ data: [] })),
        api.get('/pets').catch(() => ({ data: [] })),
        api.get('/reviews/client/me').catch(() => ({ data: [] })),
        api.get('/care-requests/my-requests').catch(() => ({ data: [] })),
        api.get('/proposals/received').catch(() => ({ data: [] })),
        api.get('/chat/conversations').catch(() => ({ data: [] }))
      ]);
      
      // Redirect providers to their dashboard
      if (userRes.data.role === 'provider' || userRes.data.provider) {
        navigate('/provider/dashboard');
        return;
      }
      
      setUser(userRes.data);
      setSubscription(subRes.data);
      setFavorites(favRes.data);
      setPets(petRes.data);
      setClientReviews(reviewRes.data);

      setStats({
        activeRequests: (careReqRes.data || []).length,
        pendingProposals: (propRes.data || []).length,
        unreadMessages: (convRes.data || []).reduce((sum, conv) => sum + (conv.unread_count || 0), 0)
      });

      if (
        userRes.data.role !== 'admin' &&
        !userRes.data.roles?.includes('admin') &&
        !checkedOnboarding &&
        (careReqRes.data || []).length === 0
      ) {
        setShowOnboardingRequest(false);
        setCheckedOnboarding(true);
      }

      // Load sent contact requests for premium clients
      if (subRes.data?.has_subscription) {
        try {
          const crRes = await api.get('/contact-requests/sent');
          setContactRequestsSent(crRes.data);
        } catch {}
      }
    } catch { navigate('/login'); }
    finally { setLoading(false); }
  };

  const handleAddPet = async () => {
    if (
      !newPet.name.trim() ||
      !newPet.type.trim() ||
      !newPet.breed.trim() ||
      !newPet.size.trim() ||
      !newPet.age.trim() ||
      !newPet.notes.trim()
    ) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    setSavingPet(true);
    try {
      const res = await api.post('/pets', newPet);
      setPets(prev => [...prev, res.data]);
      setShowPetForm(false);
      setNewPet({ name: '', type: '', breed: '', size: '', age: '', notes: '' });
      toast.success('Mascota agregada');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingPet(false); }
  };

  const handleEditPet = async (petId) => {
    if (!editPetData.name?.trim()) { toast.error('Nombre requerido'); return; }
    setSavingPet(true);
    try {
      await api.put(`/pets/${petId}`, editPetData);
      setPets(prev => prev.map(p => p.pet_id === petId ? { ...p, ...editPetData } : p));
      setEditingPet(null);
      toast.success('Mascota actualizada');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingPet(false); }
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };


  if (loading || !user) {
    return (<div className="min-h-screen flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full animate-spin" /></div>);
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Dialog
          open={false}
          onOpenChange={(open) => {
            if (!open && showOnboardingRequest) return;
            setShowOnboardingRequest(open);
            if (open) {
              setOnboardingStep(pets.length === 0 ? 'request' : 'request');
            }
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bienvenido a U-CAN</DialogTitle>
            </DialogHeader>

            {onboardingStep === 'pet' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Primero agrega tu mascota para poder crear tu Solicitud de Cuidado.
                </p>
                <PetForm
                  onSaved={(createdPet) => {
                    const savedPet = createdPet || null;
                    if (savedPet) {
                      setPets(prev => [...prev, savedPet]);
                      setOnboardingPetId(savedPet.pet_id);
                    }
                    setOnboardingStep('request');
                  }}
                  onCancel={() => setShowOnboardingRequest(false)}
                />
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Completa tu primera Solicitud de Cuidado para que los cuidadores puedan enviarte propuestas.
                </p>
        {/* Próximamente: Actividad reciente */}
        <div className="bg-white rounded-3xl border shadow-sm p-10 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-3">
            Tu centro de control U-CAN
          </h2>

          <p className="text-gray-500 max-w-xl mx-auto">
            Revisa tus solicitudes, responde propuestas, administra tus mascotas
            y continúa conversaciones desde los accesos principales.
          </p>
        </div>

              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Header Cliente Pro */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-5">
            <h1 className="text-4xl font-bold">Mi Panel</h1>
            <span className="px-3 py-1 bg-red-50 text-[#E6202E] rounded-full text-sm font-medium">
              Cliente U-CAN
            </span>
          </div>

          <div className="bg-white rounded-3xl shadow-sm border p-6 md:p-8 mb-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-[#E6202E] mb-2">Centro de control</p>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Hola, {user.name || 'cliente'} 👋
                </h2>
                <p className="text-gray-500 mt-2">
                  Busca cuidadores, revisa tus solicitudes y mantén todo ordenado en un solo lugar.
                </p>
              </div>

              <Link to="/search">
                <Button className="bg-[#E6202E] hover:bg-[#D31522] text-white rounded-xl">
                  Buscar cuidadores
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <Link
                to="/client/requests"
                className="text-left rounded-2xl bg-red-50 border border-red-100 p-4 hover:border-[#E6202E]/40 transition-colors"
              >
                <p className="text-3xl font-black text-gray-900">{stats.activeRequests}</p>
                <p className="text-sm font-bold text-gray-800 mt-1">Solicitudes</p>
                <p className="text-xs text-gray-500">Revisa o crea solicitudes de cuidado</p>
              </Link>

              <Link
                to="/client/proposals"
                className="text-left rounded-2xl bg-gray-50 border border-gray-100 p-4 hover:border-[#E6202E]/40 transition-colors"
              >
                <p className="text-3xl font-black text-gray-900">{stats.pendingProposals}</p>
                <p className="text-sm font-bold text-gray-800 mt-1">Propuestas recibidas</p>
                <p className="text-xs text-gray-500">Cuidadores interesados</p>
              </Link>

              <Link to="/chat" className="rounded-2xl bg-gray-50 border border-gray-100 p-4 hover:border-[#E6202E]/40 transition-colors">
                <p className="text-3xl font-black text-gray-900">{stats.unreadMessages}</p>
                <p className="text-sm font-bold text-gray-800 mt-1">Mensajes nuevos</p>
                <p className="text-xs text-gray-500">Conversaciones por revisar</p>
              </Link>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-3">
            <Link to="/search" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <Search className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Buscar cuidadores</span>
            </Link>

            <Link to="/client/requests" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <Inbox className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Solicitudes</span>
            </Link>

            <Link to="/client/proposals" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <Send className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Propuestas</span>
            </Link>

            <Link to="/chat" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Mensajes</span>
            </Link>

            <Link to="/client/pets" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <Dog className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Mascotas</span>
            </Link>

            <Link to="/historial?tab=en-curso" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Mis servicios</span>
            </Link>

            <Link to="/favoritos" className="bg-white rounded-2xl p-4 shadow-sm border hover:border-[#E6202E]/40 transition-colors flex items-center gap-3">
              <Heart className="w-5 h-5 text-[#E6202E]" />
              <span className="font-semibold text-sm">Favoritos</span>
            </Link>
          </div>
        </div>

        {/* Become Carer button - only show if user doesn't have provider role */}
        {user.role !== 'provider' && user.role !== 'admin' && !user.roles?.includes('provider') && (
          <Link to="/provider/register" className="block mb-6" data-testid="become-carer-link">
            <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-dashed border-[#E6202E]/30 hover:border-[#E6202E] transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#E6202E]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">¿Quieres ser Cuidador?</h3>
                  <p className="text-sm text-gray-500">Ofrece tus servicios y empieza a ganar</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </Link>
        )}

        {/* Subscription Card */}
        <div className="mb-6">
          <SubscriptionCard userType="client" hasSubscription={subscription?.has_subscription} />
        </div>

        {subscription?.has_subscription && contactRequestsSent.length > 0 && (
          <div className="mb-6 bg-white rounded-2xl p-5 shadow-sm border">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#E6202E]" />
              Contactos directos enviados
            </h2>

            <div className="space-y-3">
              {contactRequestsSent.slice(0, 3).map(req => (
                <div key={req.request_id} className={`rounded-xl border p-4 ${
                  req.status === 'accepted'
                    ? 'bg-green-50 border-green-200'
                    : req.status === 'rejected'
                    ? 'bg-gray-50 border-gray-200 opacity-75'
                    : 'bg-blue-50 border-blue-100'
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-bold text-gray-900">
                        {req.provider_name || req.provider_business_name || 'Cuidador U-CAN'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {req.status === 'accepted'
                          ? 'Contacto aceptado · Servicio en curso'
                          : req.status === 'rejected'
                          ? 'Solicitud rechazada'
                          : 'Solicitud enviada · Esperando respuesta'}
                      </p>
                    </div>

                    {req.status === 'accepted' ? (
                      <div className="flex gap-2">
                        <Link to="/historial?tab=en-curso">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Ver servicio
                          </Button>
                        </Link>
                        <Link to="/chat">
                          <Button size="sm" variant="outline">
                            <MessageCircle className="w-4 h-4 mr-1" />
                            Abrir chat
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <span className="text-xs font-bold text-gray-500">
                        {req.status === 'rejected' ? 'Finalizada' : 'Pendiente'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navegación antigua eliminada: ahora usamos accesos rápidos arriba */}

        {/* Tab Content: Solicitudes */}
        {activeTab === 'requests' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
        {/* Próximamente: Actividad reciente */}
        <div className="bg-white rounded-3xl border shadow-sm p-10 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-3">
            Tu centro de control U-CAN
          </h2>

          <p className="text-gray-500 max-w-xl mx-auto">
            Revisa tus solicitudes, responde propuestas, administra tus mascotas
            y continúa conversaciones desde los accesos principales.
          </p>
        </div>

          </div>
        )}

        {/* Tab Content: Mis Mascotas */}
        {activeTab === 'pets' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Dog className="w-5 h-5 text-[#E6202E]" />
                Mis Mascotas
              </h2>
              <Button onClick={() => setShowPetForm(true)} className="bg-[#E6202E] hover:bg-[#D31522]">
                <Plus className="w-4 h-4 mr-2" /> Agregar
              </Button>
            </div>

        <Dialog open={showPetForm} onOpenChange={setShowPetForm}>
  <DialogContent className="max-w-lg">
    <DialogHeader>
      <DialogTitle>Agregar mascota</DialogTitle>
    </DialogHeader>

    <PetForm
      onSaved={() => {
        const returnTo = localStorage.getItem('ucan_after_pet_redirect');

        setShowPetForm(false);

        if (returnTo && returnTo.startsWith('/provider/')) {
          localStorage.removeItem('ucan_after_pet_redirect');
          navigate(returnTo, { replace: true });
          return;
        }

        loadData();
      }}
      onCancel={() => {
        const returnTo = localStorage.getItem('ucan_after_pet_redirect');

        setShowPetForm(false);

        if (returnTo && returnTo.startsWith('/provider/')) {
          localStorage.removeItem('ucan_after_pet_redirect');
          navigate(returnTo, { replace: true });
        }
      }}
    />
  </DialogContent>
</Dialog>



            {pets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Dog className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No tienes mascotas registradas</p>
                <p className="text-sm">Agrega tu primera mascota para solicitar servicios</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pets.map(pet => (
                  <div key={pet.pet_id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                    {editingPet === pet.pet_id ? (
                      <div className="space-y-2">
                        <Input value={editPetData.name} onChange={e => setEditPetData(p => ({ ...p, name: e.target.value }))} placeholder="Nombre" />
                        <div className="grid grid-cols-2 gap-2">
                          <select value={editPetData.type} onChange={e => setEditPetData(p => ({ ...p, type: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm">
                            <option value="perro">Perro</option>
                            <option value="gato">Gato</option>
                            <option value="otro">Otro</option>
                          </select>
                          <select value={editPetData.size} onChange={e => setEditPetData(p => ({ ...p, size: e.target.value }))} className="px-3 py-2 border rounded-lg text-sm">
                            <option value="pequeno">Pequeño</option>
                            <option value="mediano">Mediano</option>
                            <option value="grande">Grande</option>
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleEditPet(pet.pet_id)} disabled={savingPet} className="bg-[#E6202E]">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingPet(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center overflow-hidden">
  {pet.photo ? (
    <img
      src={`${process.env.REACT_APP_BACKEND_URL}${pet.photo}`}
      alt={pet.name}
      className="w-full h-full object-cover"
    />
  ) : (
    <span className="text-2xl">
      {pet.type === 'perro' || pet.species === 'perro' ? '🐕' : pet.type === 'gato' || pet.species === 'gato' ? '🐈' : '🐾'}
    </span>
  )}
</div>
                        <div className="flex-1">
                          <h4 className="font-bold">{pet.name}</h4>
                          <p className="text-sm text-gray-500">{pet.breed || pet.type} • {PET_SIZE_LABEL[pet.size]}</p>
                          {pet.age && <p className="text-xs text-gray-400">{pet.age}</p>}
                        </div>
                        <button
                          onClick={() => { setEditingPet(pet.pet_id); setEditPetData({ name: pet.name, type: pet.type, breed: pet.breed, size: pet.size, age: pet.age, notes: pet.notes }); }}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <Pencil className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab Content: Favoritos */}
        {activeTab === 'favorites' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#E6202E]" />
              Mis Favoritos
            </h2>
            {favorites.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No tienes favoritos aún</p>
                <Link to="/search" className="text-[#E6202E] hover:underline text-sm">Buscar cuidadores</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {favorites.map(fav => (
                  <Link key={fav.provider_id} to={`/provider/${fav.provider_id}`} className="border rounded-xl p-4 hover:shadow-md transition-shadow flex items-center gap-4">
                    <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center">
                      {fav.photos?.[0] ? (
                        <img src={fav.photos[0]} alt={firstNameOnly(fav.business_name)} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <PawPrint className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold">{firstNameOnly(fav.business_name)}</h4>
                      <p className="text-sm text-gray-500">{fav.comuna}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab Content: Mensajes */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#E6202E]" />
              Mensajes
            </h2>
            <div className="text-center py-12 text-gray-500">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Tus conversaciones con cuidadores</p>
              <Link to="/chat" className="text-[#E6202E] hover:underline text-sm">Ir al chat</Link>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Dashboard;
